import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Stack,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Insights as InsightsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  Psychology as PsychologyIcon,
  BarChart as BarChartIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
  Recommend as RecommendIcon,
  Business as BusinessIcon,
  Create as CreateIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LineChart as LineChartIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';
import { useToast } from '../contexts/ToastContext';
import { mlAnalysisAPI } from '../services/api';
import { competitorsAPI } from '../services/api';
import { rankingTrackingAPI } from '../services/api';
import { useClients } from '../contexts/ClientContext';
import ContentSuggestionsPanel from '../components/ContentSuggestionsPanel';
import ClientScopedGate from '../components/ClientScopedGate';
import MlEstimateBadge, {
  MlMetricLabel,
  MlSectionTitle,
} from '../components/MlEstimateBadge';
import { useScopedClientSelection } from '../hooks/useScopedClientSelection';
import { ClientSelector, PageHeader } from '../components/primitives';

import CompetitorDetailView from './mlInsights/CompetitorDetailView';
import MlOverviewTab from './mlInsights/MlOverviewTab';
import MlModelMonitoringTab from './mlInsights/MlModelMonitoringTab';
import { ML_CHART_COLORS } from './mlInsights/constants';
import {
  getSentimentColor,
  getSentimentLabel,
  getQualityColor,
  getMarketShareChartData,
  getQualityScoreChartData,
  getPlatformPresenceChartData,
  getSentimentPieData,
} from './mlInsights/helpers';

const ML_INSIGHTS_TAB_KEYS = {
  overview: 0,
  rankings: 1,
};

const MLInsights = () => {
  const { showSuccess, showError } = useToast();
  const [searchParams] = useSearchParams();
  const { clients, loading: clientsLoading } = useClients();
  const { selectedClient, clientId, selectClient } = useScopedClientSelection();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Client-level state
  const [clientSummary, setClientSummary] = useState(null);
  const [competitiveOverview, setCompetitiveOverview] = useState(null);
  const [contentAnalysis, setContentAnalysis] = useState(null);
  const [sentimentAnalysis, setSentimentAnalysis] = useState(null);
  const [platformPresence, setPlatformPresence] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [contentBreakdown, setContentBreakdown] = useState(null);
  const [contentInsights, setContentInsights] = useState(null);
  const [seoAnalysis, setSeoAnalysis] = useState(null);
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [gapModalOpen, setGapModalOpen] = useState(false);
  const [gapModalData, setGapModalData] = useState(null);
  const [abEvaluation, setAbEvaluation] = useState(null);
  const [abEvaluationLoading, setAbEvaluationLoading] = useState(false);
  const [weeklyLearnings, setWeeklyLearnings] = useState(null);
  const [weeklyLearningsLoading, setWeeklyLearningsLoading] = useState(false);
  const [weeklyLearningsDays, setWeeklyLearningsDays] = useState(7);
  const [captionEditLearning, setCaptionEditLearning] = useState(null);
  const [captionEditLearningLoading, setCaptionEditLearningLoading] = useState(false);
  const [driftAlerts, setDriftAlerts] = useState([]);
  // Ranking tracking state
  const [keywords, setKeywords] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [cseStatus, setCseStatus] = useState(null);
  const [keywordDialogOpen, setKeywordDialogOpen] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState(null);
  const [newKeyword, setNewKeyword] = useState({
    keyword: '',
    location: '',
    priority: 'medium',
    target_position: null,
  });
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [deleteKeywordModal, setDeleteKeywordModal] = useState({
    open: false,
    keywordId: null,
    keywordText: '',
  });

  // Competitor drill-down state
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [competitorDetails, setCompetitorDetails] = useState(null);
  const [competitorPosts, setCompetitorPosts] = useState([]);
  const [competitorAnalytics, setCompetitorAnalytics] = useState([]);
  const [showCompetitorView, setShowCompetitorView] = useState(false);

  useEffect(() => {
    const t = (searchParams.get('tab') || 'overview').toLowerCase();
    if (t in ML_INSIGHTS_TAB_KEYS) {
      setActiveTab(ML_INSIGHTS_TAB_KEYS[t]);
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedClient) {
      loadClientData(selectedClient.id);
    }
  }, [selectedClient]);

  useEffect(() => {
    if (selectedClient && activeTab === 1) {
      loadKeywords();
      loadRankings();
    }
  }, [selectedClient, activeTab]);

  useEffect(() => {
    if (activeTab === 10) {
      loadABEvaluation();
      loadDriftAlerts();
      loadWeeklyLearnings();
      loadCaptionEditLearning();
    }
  }, [activeTab]);

  const loadCaptionEditLearning = async () => {
    try {
      setCaptionEditLearningLoading(true);
      const data = await mlAnalysisAPI.getCaptionEditLearningStats();
      setCaptionEditLearning(data);
    } catch (err) {
      console.error('Load caption edit learning failed:', err);
      setCaptionEditLearning(null);
    } finally {
      setCaptionEditLearningLoading(false);
    }
  };

  const loadDriftAlerts = async () => {
    try {
      const data = await mlAnalysisAPI.getDriftAlerts();
      setDriftAlerts(data.alerts || []);
    } catch {
      setDriftAlerts([]);
    }
  };

  const loadWeeklyLearnings = async (days = weeklyLearningsDays) => {
    try {
      setWeeklyLearningsLoading(true);
      const data = await mlAnalysisAPI.getWeeklyLearnings(days);
      setWeeklyLearnings(data);
    } catch (err) {
      console.error('Load weekly learnings failed:', err);
      setWeeklyLearnings(null);
    } finally {
      setWeeklyLearningsLoading(false);
    }
  };

  const handleWeeklyLearningsPeriodChange = (newDays) => {
    setWeeklyLearningsDays(newDays);
    loadWeeklyLearnings(newDays);
  };

  const loadABEvaluation = async () => {
    try {
      setAbEvaluationLoading(true);
      const data = await mlAnalysisAPI.getABEvaluation();
      setAbEvaluation(data);
    } catch (err) {
      console.error('Load A/B evaluation failed:', err);
      showError('Failed to load model monitoring data');
      setAbEvaluation(null);
    } finally {
      setAbEvaluationLoading(false);
    }
  };

  const handleTriggerBackfill = async () => {
    try {
      await mlAnalysisAPI.triggerABBackfill();
      showSuccess('Backfill queued. Metrics will update after completion.');
    } catch (err) {
      showError('Failed to trigger backfill');
    }
  };

  useEffect(() => {
    if (selectedCompetitor) {
      loadCompetitorDetails(selectedCompetitor.competitor_id);
    }
  }, [selectedCompetitor]);

  const loadClientData = async (clientId) => {
    try {
      setLoading(true);

      // Load all client-level data in parallel
      const [
        summary,
        overview,
        content,
        sentiment,
        platforms,
        recs,
        breakdown,
        insights,
        seo,
        gap,
      ] = await Promise.all([
        mlAnalysisAPI.getClientSummary(clientId).catch(() => null),
        mlAnalysisAPI.getClientCompetitiveOverview(clientId).catch(() => null),
        mlAnalysisAPI.getClientContentAnalysis(clientId).catch(() => null),
        mlAnalysisAPI.getClientSentimentAnalysis(clientId).catch(() => null),
        mlAnalysisAPI.getClientPlatformPresence(clientId).catch(() => null),
        mlAnalysisAPI.getClientRecommendations(clientId).catch(() => null),
        mlAnalysisAPI.getClientContentBreakdown(clientId).catch(() => null),
        mlAnalysisAPI.getClientContentInsights(clientId).catch(() => null),
        mlAnalysisAPI.getClientSEOAnalysis(clientId).catch(() => null),
        mlAnalysisAPI.getClientGapAnalysis(clientId).catch(() => null),
      ]);

      setClientSummary(summary);
      setCompetitiveOverview(overview);
      setContentAnalysis(content);
      setSentimentAnalysis(sentiment);
      setPlatformPresence(platforms);
      setRecommendations(recs);
      setContentBreakdown(breakdown);
      setContentInsights(insights);
      setSeoAnalysis(seo);
      setGapAnalysis(gap);
    } catch (error) {
      console.error('Error loading client data:', error);
      showError('Failed to load client insights');
    } finally {
      setLoading(false);
    }
  };

  // Load keywords and rankings for ranking tracking
  const loadKeywords = async () => {
    if (!selectedClient) return;
    try {
      setKeywordsLoading(true);
      const data = await rankingTrackingAPI.getKeywords(selectedClient.id);
      setKeywords(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading keywords:', error);
      showError('Failed to load keywords');
    } finally {
      setKeywordsLoading(false);
    }
  };

  const loadRankings = async () => {
    if (!selectedClient) return;
    try {
      setRankingsLoading(true);
      const [data, status] = await Promise.all([
        rankingTrackingAPI.getClientRankings(selectedClient.id, 365),
        rankingTrackingAPI.getCseStatus().catch(() => null),
      ]);
      setRankings(Array.isArray(data) ? data : []);
      setCseStatus(status);
    } catch (error) {
      console.error('Error loading rankings:', error);
      showError('Failed to load ranking history');
    } finally {
      setRankingsLoading(false);
    }
  };

  const handleAddKeyword = async () => {
    if (!selectedClient || !newKeyword.keyword.trim()) {
      showError('Please enter a keyword');
      return;
    }
    try {
      await rankingTrackingAPI.createKeyword(selectedClient.id, newKeyword);
      showSuccess('Keyword added successfully');
      setKeywordDialogOpen(false);
      setNewKeyword({
        keyword: '',
        location: '',
        priority: 'medium',
        target_position: null,
      });
      loadKeywords();
    } catch (error) {
      console.error('Error adding keyword:', error);
      showError('Failed to add keyword');
    }
  };

  const handleDeleteKeywordClick = (keyword) => {
    setDeleteKeywordModal({
      open: true,
      keywordId: keyword.id,
      keywordText: keyword.keyword || '',
    });
  };

  const handleDeleteKeywordConfirm = async () => {
    const { keywordId } = deleteKeywordModal;
    if (!keywordId) return;
    try {
      await rankingTrackingAPI.deleteKeyword(keywordId);
      showSuccess('Keyword deleted successfully');
      setDeleteKeywordModal({ open: false, keywordId: null, keywordText: '' });
      loadKeywords();
      loadRankings();
    } catch (error) {
      console.error('Error deleting keyword:', error);
      showError('Failed to delete keyword');
    }
  };

  const handleDeleteKeywordCancel = () => {
    setDeleteKeywordModal({ open: false, keywordId: null, keywordText: '' });
  };

  const handleCheckRanking = async (keywordId) => {
    try {
      await rankingTrackingAPI.checkKeywordRanking(keywordId);
      showSuccess(
        'Ranking check queued. Results will appear when the task finishes (ensure Celery worker is running).'
      );
      // Poll for results: task can take 10–15s with multi-page fetch
      [5000, 10000, 15000].forEach((ms) =>
        setTimeout(() => loadRankings(), ms)
      );
    } catch (error) {
      console.error('Error checking ranking:', error);
      showError('Failed to check ranking');
    }
  };

  const handleCheckAllRankings = async () => {
    if (!selectedClient) return;
    try {
      await rankingTrackingAPI.checkAllRankings(selectedClient.id);
      showSuccess(
        'Ranking checks queued for all keywords. Results will appear when tasks finish (ensure Celery worker is running).'
      );
      // Poll for results: multiple keywords take longer
      [10000, 20000, 30000, 45000].forEach((ms) =>
        setTimeout(() => loadRankings(), ms)
      );
    } catch (error) {
      console.error('Error checking rankings:', error);
      showError('Failed to check rankings');
    }
  };

  const handleSuggestKeywords = async () => {
    if (!selectedClient) return;
    try {
      setSuggestionsLoading(true);
      setSuggestionsOpen(true);
      const data = await rankingTrackingAPI.getSuggestedKeywords(
        selectedClient.id,
        15
      );
      setSuggestions(data || []);
    } catch (error) {
      console.error('Error loading suggestions:', error);
      showError('Failed to load keyword suggestions');
      setSuggestionsOpen(false);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleAddSuggestion = async (suggestion) => {
    if (!selectedClient) return;
    try {
      await rankingTrackingAPI.createKeyword(selectedClient.id, {
        keyword: suggestion.keyword,
        location: suggestion.location || undefined,
        priority: 'medium',
      });
      showSuccess(`Added "${suggestion.keyword}"`);
      loadKeywords();
      setSuggestions((prev) =>
        prev.map((s) =>
          s.keyword === suggestion.keyword &&
          (s.location || '') === (suggestion.location || '')
            ? { ...s, already_added: true }
            : s
        )
      );
    } catch (error) {
      console.error('Error adding keyword:', error);
      showError('Failed to add keyword');
    }
  };

  const loadCompetitorDetails = async (competitorId) => {
    try {
      setLoading(true);

      const [marketShare, qualityScore, sentiment, insights, posts, analytics] =
        await Promise.all([
          mlAnalysisAPI
            .getCompetitorMarketShare(competitorId)
            .catch(() => null),
          mlAnalysisAPI
            .getCompetitorQualityScore(competitorId)
            .catch(() => null),
          mlAnalysisAPI.getCompetitorSentiment(competitorId).catch(() => null),
          mlAnalysisAPI
            .getCompetitorCompetitiveInsights(competitorId)
            .catch(() => null),
          competitorsAPI
            .getCompetitorPosts(competitorId, { days: 365, limit: 100 })
            .catch(() => []),
          competitorsAPI
            .getCompetitorAnalytics(competitorId, { days: 365 })
            .catch(() => []),
        ]);

      setCompetitorDetails({
        marketShare,
        qualityScore,
        sentiment,
        insights,
      });
      setCompetitorPosts(Array.isArray(posts) ? posts : []);
      setCompetitorAnalytics(Array.isArray(analytics) ? analytics : []);
    } catch (error) {
      console.error('Error loading competitor details:', error);
      showError('Failed to load competitor details');
    } finally {
      setLoading(false);
    }
  };

  const handleCompetitorClick = (competitor) => {
    if (!competitor?.competitor_id) return;
    const normalized = {
      competitor_id: competitor.competitor_id,
      name: competitor.name || competitor.competitor_name || `Competitor #${competitor.competitor_id}`,
      market_share: competitor.market_share,
      content_quality_score:
        competitor.content_quality_score ?? competitor.quality_score,
      brand_sentiment:
        competitor.brand_sentiment ?? competitor.sentiment,
    };
    setCompetitorDetails(null);
    setCompetitorPosts([]);
    setCompetitorAnalytics([]);
    setSelectedCompetitor(normalized);
    setShowCompetitorView(true);
  };

  const handleBackToClientView = () => {
    setShowCompetitorView(false);
    setSelectedCompetitor(null);
    setCompetitorDetails(null);
    setCompetitorPosts([]);
    setCompetitorAnalytics([]);
  };


  if (clientsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Competitor drill-down view (preserve parent activeTab for back navigation)
  if (showCompetitorView && selectedCompetitor) {
    if (!competitorDetails) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      );
    }
    return (
      <CompetitorDetailView
        selectedClient={selectedClient}
        selectedCompetitor={selectedCompetitor}
        competitorDetails={competitorDetails}
        competitorPosts={competitorPosts}
        competitorAnalytics={competitorAnalytics}
        onBack={handleBackToClientView}
      />
    );
  }

  // Client-level view
  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="ML Insights & Analysis"
        subtitle="View aggregated competitive intelligence and ML analysis by client"
        icon={<InsightsIcon />}
        actions={
          <>
            <ClientSelector
              clients={Array.isArray(clients) ? clients : []}
              value={selectedClient?.id}
              onChange={(client) => client && selectClient(client)}
            />
            <IconButton
              onClick={() => selectedClient && loadClientData(selectedClient.id)}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </>
        }
      />

      <ClientScopedGate
        clientId={clientId}
        minHeight={400}
        requirePosts={false}
        acceptGsc
      >
        {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Client Summary Cards */}
          {clientSummary && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Total Competitors
                    </Typography>
                    <Typography variant="h4">
                      {clientSummary.total_competitors || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Total Followers
                    </Typography>
                    <Typography variant="h4">
                      {(clientSummary.total_followers || 0).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <MlMetricLabel basis="quality_rubric">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Avg Quality Score
                      </Typography>
                    </MlMetricLabel>
                    <Typography variant="h4" color="success.main">
                      {((clientSummary.avg_quality_score || 0) * 100).toFixed(
                        0
                      )}
                      %
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <MlMetricLabel basis="sentiment_nlp">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Avg Sentiment
                      </Typography>
                    </MlMetricLabel>
                    <Typography
                      variant="h4"
                      color={getSentimentColor(
                        clientSummary.avg_sentiment || 0.5
                      )}
                    >
                      {getSentimentLabel(clientSummary.avg_sentiment || 0.5)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Tabs for different views */}
          <Card>
            <CardContent>
              <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                sx={{ mb: 3 }}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Overview" />
                <Tab label="Rankings" />
                <Tab label="Content Analysis" />
                <Tab label="Content Breakdown" />
                <Tab label="Content Insights" />
                <Tab label="SEO Analysis" />
                <Tab label="Gap Analysis" />
                <Tab label="Sentiment" />
                <Tab label="Platform Presence" />
                <Tab label="Recommendations" />
                <Tab label="Model Monitoring" />
              </Tabs>

              {/* Overview Tab */}
              {activeTab === 0 && clientSummary && (
                <MlOverviewTab
                  clientSummary={clientSummary}
                  competitiveOverview={competitiveOverview}
                  onCompetitorClick={handleCompetitorClick}
                />
              )}


              {/* Rankings Tab - Keyword Ranking Tracking */}
              {activeTab === 1 && selectedClient && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 3,
                    }}
                  >
                    <Typography variant="h6">
                      Keyword Ranking Tracking
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleCheckAllRankings}
                        disabled={keywords.length === 0 || rankingsLoading}
                      >
                        Check All Rankings
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<SearchIcon />}
                        onClick={handleSuggestKeywords}
                        disabled={!selectedClient}
                      >
                        Suggest Keywords
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setKeywordDialogOpen(true)}
                      >
                        Add Keyword
                      </Button>
                    </Box>
                  </Box>

                  {(cseStatus?.using_mock ||
                    cseStatus?.banner_message ||
                    rankings.some((r) => r.has_mock_data)) && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {cseStatus?.banner_message ||
                        'Rankings using estimated data — set SERPER_API_KEY for live results'}
                    </Alert>
                  )}

                  {keywordsLoading || rankingsLoading ? (
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', p: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : keywords.length === 0 ? (
                    <Card>
                      <CardContent>
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <SearchIcon
                            sx={{
                              fontSize: 64,
                              color: 'text.secondary',
                              mb: 2,
                            }}
                          />
                          <Typography variant="h6" gutterBottom>
                            No Keywords Tracked
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 3 }}
                          >
                            Add keywords to track your Google search rankings
                            over time
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setKeywordDialogOpen(true)}
                          >
                            Add Your First Keyword
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      {/* Ranking Summary Cards */}
                      <Grid container spacing={3} sx={{ mb: 3 }}>
                        {rankings
                          .filter((r) => r.snapshots && r.snapshots.length > 0)
                          .slice(0, 3)
                          .map((ranking) => (
                            <Grid item xs={12} md={4} key={ranking.keyword.id}>
                              <Card>
                                <CardContent>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    gutterBottom
                                  >
                                    {ranking.keyword.keyword}
                                    {ranking.keyword.location &&
                                      ` (${ranking.keyword.location})`}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2,
                                      mt: 2,
                                    }}
                                  >
                                    <Box>
                                      <Typography
                                        variant="h4"
                                        fontWeight="bold"
                                      >
                                        {ranking.current_position || 'N/A'}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Current Position
                                      </Typography>
                                    </Box>
                                    {ranking.position_change !== null &&
                                      ranking.position_change !== 0 && (
                                        <Box>
                                          {ranking.position_change < 0 ? (
                                            <TrendingUpIcon color="success" />
                                          ) : (
                                            <TrendingDownIcon color="error" />
                                          )}
                                          <Typography
                                            variant="body2"
                                            color={
                                              ranking.position_change < 0
                                                ? 'success.main'
                                                : 'error.main'
                                            }
                                          >
                                            {ranking.position_change < 0
                                              ? '+'
                                              : ''}
                                            {Math.abs(ranking.position_change)}
                                          </Typography>
                                        </Box>
                                      )}
                                  </Box>
                                  {ranking.best_position && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ mt: 1, display: 'block' }}
                                    >
                                      Best: {ranking.best_position} | Avg:{' '}
                                      {ranking.average_position?.toFixed(1) ||
                                        'N/A'}
                                    </Typography>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                      </Grid>

                      {/* Ranking Trends Chart */}
                      {(Array.isArray(rankings) ? rankings : []).filter(
                        (r) => r.snapshots && r.snapshots.length > 0
                      ).length > 0 && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Ranking Trends (Last 30 Days)
                            </Typography>
                            <ResponsiveContainer width="100%" height={400}>
                              <AreaChart>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="date"
                                  type="category"
                                  allowDuplicatedCategory={false}
                                />
                                <YAxis reversed domain={[1, 100]} />
                                <RechartsTooltip />
                                <Legend />
                                {(Array.isArray(rankings) ? rankings : [])
                                  .filter(
                                    (r) => r.snapshots && r.snapshots.length > 0
                                  )
                                  .slice(0, 5)
                                  .map((ranking, idx) => {
                                    const chartData = ranking.snapshots
                                      .slice(0, 30)
                                      .reverse()
                                      .map((snapshot) => ({
                                        date: new Date(
                                          snapshot.checked_at
                                        ).toLocaleDateString(),
                                        [ranking.keyword.keyword]:
                                          snapshot.position,
                                      }));
                                    return (
                                      <Area
                                        key={ranking.keyword.id}
                                        type="monotone"
                                        dataKey={ranking.keyword.keyword}
                                        stackId="1"
                                        stroke={ML_CHART_COLORS[idx % ML_CHART_COLORS.length]}
                                        fill={ML_CHART_COLORS[idx % ML_CHART_COLORS.length]}
                                        data={chartData}
                                      />
                                    );
                                  })}
                              </AreaChart>
                            </ResponsiveContainer>
                          </CardContent>
                        </Card>
                      )}

                      {/* Keywords and Rankings Table */}
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>
                            Keyword Rankings
                          </Typography>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Keyword</TableCell>
                                  <TableCell>Location</TableCell>
                                  <TableCell>Current</TableCell>
                                  <TableCell>Best</TableCell>
                                  <TableCell>Average</TableCell>
                                  <TableCell>Change</TableCell>
                                  <TableCell>Days Tracked</TableCell>
                                  <TableCell>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(Array.isArray(keywords) ? keywords : []).map((keyword) => {
                                  // Find ranking data for this keyword
                                  const safeRankings = Array.isArray(rankings) ? rankings : [];
                                  const ranking = safeRankings.find(
                                    (r) => r.keyword.id === keyword.id
                                  );
                                  const hasRankingData =
                                    ranking &&
                                    ranking.snapshots &&
                                    ranking.snapshots.length > 0;

                                  return (
                                    <TableRow key={keyword.id} hover>
                                      <TableCell>
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                        >
                                          {keyword.keyword}
                                        </Typography>
                                        <Chip
                                          label={keyword.priority}
                                          size="small"
                                          color={
                                            keyword.priority === 'high'
                                              ? 'error'
                                              : keyword.priority === 'medium'
                                              ? 'warning'
                                              : 'default'
                                          }
                                          sx={{ mt: 0.5 }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        {keyword.location || 'Global'}
                                      </TableCell>
                                      <TableCell>
                                        {hasRankingData &&
                                        ranking.current_position ? (
                                          <Chip
                                            label={ranking.current_position}
                                            color={
                                              ranking.current_position <= 10
                                                ? 'success'
                                                : ranking.current_position <= 30
                                                ? 'warning'
                                                : 'default'
                                            }
                                            size="small"
                                          />
                                        ) : hasRankingData ? (
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            Not found
                                          </Typography>
                                        ) : (
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontStyle: 'italic' }}
                                          >
                                            Not checked yet
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {hasRankingData
                                          ? ranking.best_position || 'N/A'
                                          : 'N/A'}
                                      </TableCell>
                                      <TableCell>
                                        {hasRankingData &&
                                        ranking.average_position
                                          ? ranking.average_position.toFixed(1)
                                          : 'N/A'}
                                      </TableCell>
                                      <TableCell>
                                        {hasRankingData &&
                                        ranking.position_change !== null &&
                                        ranking.position_change !== 0 ? (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 0.5,
                                            }}
                                          >
                                            {ranking.position_change < 0 ? (
                                              <TrendingUpIcon
                                                color="success"
                                                fontSize="small"
                                              />
                                            ) : (
                                              <TrendingDownIcon
                                                color="error"
                                                fontSize="small"
                                              />
                                            )}
                                            <Typography
                                              variant="body2"
                                              color={
                                                ranking.position_change < 0
                                                  ? 'success.main'
                                                  : 'error.main'
                                              }
                                            >
                                              {ranking.position_change < 0
                                                ? '+'
                                                : ''}
                                              {Math.abs(
                                                ranking.position_change
                                              )}
                                            </Typography>
                                          </Box>
                                        ) : (
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            -
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {hasRankingData
                                          ? ranking.days_tracked
                                          : 0}
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleCheckRanking(keyword.id)
                                            }
                                            title="Check ranking now"
                                          >
                                            <RefreshIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleDeleteKeywordClick(keyword)
                                            }
                                            title="Delete keyword"
                                            color="error"
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </Box>
              )}

              {/* Content Analysis Tab */}
              {activeTab === 2 && contentAnalysis && (
                <Box>
                  <MlSectionTitle basis="quality_rubric">
                    Content Quality Analysis
                  </MlSectionTitle>

                  {/* Quality Score Chart */}
                  {getQualityScoreChartData(contentAnalysis).length > 0 && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <MlSectionTitle
                          basis="quality_rubric"
                          variant="subtitle1"
                        >
                          Content Quality Scores
                        </MlSectionTitle>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={getQualityScoreChartData(contentAnalysis)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="name"
                              angle={-45}
                              textAnchor="end"
                              height={100}
                            />
                            <YAxis />
                            <RechartsTooltip />
                            <Legend />
                            <Bar
                              dataKey="qualityScore"
                              fill="#00C49F"
                              name="Quality Score (%)"
                            />
                            <Bar
                              dataKey="engagement"
                              fill="#8884d8"
                              name="Avg Engagement"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <MlSectionTitle basis="quality_rubric" variant="subtitle1">
                            Top Performers
                          </MlSectionTitle>
                          <List>
                            {(contentAnalysis?.top_performers ?? []).map(
                              (performer, idx) => (
                                <ListItem
                                  key={idx}
                                  disablePadding
                                  secondaryAction={
                                    <Chip
                                      label={`#${idx + 1}`}
                                      color="primary"
                                      size="small"
                                    />
                                  }
                                >
                                  <ListItemButton
                                    onClick={() => handleCompetitorClick(performer)}
                                  >
                                    <ListItemText
                                      primary={performer.name}
                                      secondary={
                                        <>
                                          Quality:{' '}
                                          {(
                                            (performer.quality_score || 0) * 100
                                          ).toFixed(1)}
                                          % | Engagement:{' '}
                                          {performer.avg_engagement?.toFixed(0) ||
                                            0}{' '}
                                          | Posts: {performer.posts_count || 0}
                                        </>
                                      }
                                    />
                                  </ListItemButton>
                                </ListItem>
                              )
                            )}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <MlSectionTitle basis="quality_rubric" variant="subtitle1">
                            Bottom Performers
                          </MlSectionTitle>
                          <List>
                            {(contentAnalysis?.bottom_performers ?? []).map(
                              (performer, idx) => (
                                <ListItem
                                  key={idx}
                                  disablePadding
                                  secondaryAction={
                                    <Chip
                                      label={`#${idx + 1}`}
                                      color="error"
                                      size="small"
                                    />
                                  }
                                >
                                  <ListItemButton
                                    onClick={() => handleCompetitorClick(performer)}
                                  >
                                    <ListItemText
                                      primary={performer.name}
                                      secondary={
                                        <>
                                          Quality:{' '}
                                          {(
                                            (performer.quality_score || 0) * 100
                                          ).toFixed(1)}
                                          % | Engagement:{' '}
                                          {performer.avg_engagement?.toFixed(0) ||
                                            0}{' '}
                                          | Posts: {performer.posts_count || 0}
                                        </>
                                      }
                                    />
                                  </ListItemButton>
                                </ListItem>
                              )
                            )}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Content Breakdown Tab */}
              {activeTab === 3 && contentBreakdown && (
                <Box>
                  <MlSectionTitle basis="pattern_analysis">
                    Detailed Content Breakdown Analysis
                  </MlSectionTitle>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Detailed analysis of visual patterns, captions, hashtags,
                    topics, CTAs, posting patterns, and engagement triggers
                    across all competitors.
                    {contentBreakdown.total_posts > 0
                      ? ` Analyzing ${contentBreakdown.total_posts} posts.`
                      : ' No posts available yet.'}
                  </Alert>

                  {contentBreakdown.total_posts > 0 ? (
                    <>
                      {/* Visual Patterns */}
                      {contentBreakdown.visual_patterns && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Visual Content Patterns
                            </Typography>
                            {contentBreakdown.visual_patterns
                              .top_visual_type && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Top Visual Type:{' '}
                                  <strong>
                                    {contentBreakdown.visual_patterns.top_visual_type
                                      .replace(/_/g, ' ')
                                      .toUpperCase()}
                                  </strong>
                                  (
                                  {contentBreakdown.visual_patterns.visual_type_percentage?.toFixed(
                                    1
                                  )}
                                  % of posts)
                                </Typography>
                                {contentBreakdown.visual_patterns
                                  .average_carousel_slides > 0 && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Average Carousel Slides:{' '}
                                    {
                                      contentBreakdown.visual_patterns
                                        .average_carousel_slides
                                    }
                                  </Typography>
                                )}
                              </Box>
                            )}
                            {(contentBreakdown?.visual_patterns?.insights ?? []).length > 0 && (
                                <List dense>
                                  {(contentBreakdown?.visual_patterns?.insights ?? []).map(
                                    (insight, idx) => (
                                      <ListItem key={idx}>
                                        <ListItemText primary={insight} />
                                      </ListItem>
                                    )
                                  )}
                                </List>
                              )}
                          </CardContent>
                        </Card>
                      )}

                      {/* Caption Patterns */}
                      {contentBreakdown.caption_patterns && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Caption Patterns
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Average Length:{' '}
                                  {
                                    contentBreakdown.caption_patterns
                                      .average_caption_length
                                  }{' '}
                                  characters
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Top Structure:{' '}
                                  {contentBreakdown.caption_patterns.top_caption_structure
                                    ?.replace(/_/g, ' ')
                                    .toUpperCase() || 'N/A'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Top Tone:{' '}
                                  {contentBreakdown.caption_patterns.top_caption_tone?.toUpperCase() ||
                                    'N/A'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Average Emojis:{' '}
                                  {contentBreakdown.caption_patterns.average_emoji_count?.toFixed(
                                    1
                                  )}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Average Line Breaks:{' '}
                                  {contentBreakdown.caption_patterns.line_breaks_usage?.average?.toFixed(
                                    1
                                  ) || 0}
                                </Typography>
                              </Grid>
                            </Grid>
                            {(contentBreakdown?.caption_patterns?.insights ?? []).length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <List dense>
                                    {(contentBreakdown?.caption_patterns?.insights ?? []).map(
                                      (insight, idx) => (
                                        <ListItem key={idx}>
                                          <ListItemText primary={insight} />
                                        </ListItem>
                                      )
                                    )}
                                  </List>
                                </Box>
                              )}
                          </CardContent>
                        </Card>
                      )}

                      {/* Hashtag Patterns */}
                      {contentBreakdown.hashtag_patterns &&
                        contentBreakdown.hashtag_patterns.hashtag_usage && (
                          <Card sx={{ mb: 3 }}>
                            <CardContent>
                              <Typography variant="subtitle1" gutterBottom>
                                Hashtag Patterns
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                              >
                                Average Hashtags:{' '}
                                {contentBreakdown.hashtag_patterns.hashtag_usage.average_count?.toFixed(
                                  1
                                )}
                              </Typography>
                              {contentBreakdown.hashtag_patterns.top_hashtags &&
                                contentBreakdown.hashtag_patterns.top_hashtags
                                  .length > 0 && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" gutterBottom>
                                      Top Hashtags:
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                      }}
                                    >
                                      {contentBreakdown.hashtag_patterns.top_hashtags
                                        .slice(0, 20)
                                        .map((tag, idx) => (
                                          <Chip
                                            key={idx}
                                            label={`#${tag.hashtag} (${tag.count})`}
                                            size="small"
                                          />
                                        ))}
                                    </Box>
                                  </Box>
                                )}
                              {(contentBreakdown?.hashtag_patterns?.insights ?? []).length > 0 && (
                                  <Box sx={{ mt: 2 }}>
                                    <List dense>
                                      {(contentBreakdown?.hashtag_patterns?.insights ?? []).map(
                                        (insight, idx) => (
                                          <ListItem key={idx}>
                                            <ListItemText primary={insight} />
                                          </ListItem>
                                        )
                                      )}
                                    </List>
                                  </Box>
                                )}
                            </CardContent>
                          </Card>
                        )}

                      {/* Topic Patterns */}
                      {contentBreakdown.topic_patterns && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Content Topic Patterns
                            </Typography>
                            {contentBreakdown.topic_patterns
                              .top_content_pillar && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                              >
                                Top Content Pillar:{' '}
                                <strong>
                                  {contentBreakdown.topic_patterns.top_content_pillar
                                    .replace(/_/g, ' ')
                                    .toUpperCase()}
                                </strong>
                                (
                                {contentBreakdown.topic_patterns.pillar_percentage?.toFixed(
                                  1
                                )}
                                % of posts)
                              </Typography>
                            )}
                            {contentBreakdown.topic_patterns
                              .topic_performance &&
                              contentBreakdown.topic_patterns.topic_performance
                                .length > 0 && (
                                <TableContainer sx={{ mt: 2 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Topic</TableCell>
                                        <TableCell align="right">
                                          Posts
                                        </TableCell>
                                        <TableCell align="right">
                                          Avg Engagement
                                        </TableCell>
                                        <TableCell align="right">
                                          Percentage
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {contentBreakdown.topic_patterns.topic_performance
                                        .slice(0, 10)
                                        .map((topic, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>
                                              {topic.topic
                                                .replace(/_/g, ' ')
                                                .toUpperCase()}
                                            </TableCell>
                                            <TableCell align="right">
                                              {topic.posts_count}
                                            </TableCell>
                                            <TableCell align="right">
                                              {topic.avg_engagement?.toFixed(
                                                0
                                              ) || 0}
                                            </TableCell>
                                            <TableCell align="right">
                                              {topic.percentage?.toFixed(1)}%
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              )}
                          </CardContent>
                        </Card>
                      )}

                      {/* CTA Patterns */}
                      {contentBreakdown.cta_patterns && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Call-to-Action (CTA) Patterns
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Posts with CTAs:{' '}
                              {contentBreakdown.cta_patterns.cta_usage_percentage?.toFixed(
                                1
                              )}
                              %
                            </Typography>
                            {contentBreakdown.cta_patterns.top_cta_type && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                              >
                                Top CTA Type:{' '}
                                <strong>
                                  {contentBreakdown.cta_patterns.top_cta_type
                                    .replace(/_/g, ' ')
                                    .toUpperCase()}
                                </strong>
                              </Typography>
                            )}
                            {contentBreakdown.cta_patterns.cta_performance &&
                              contentBreakdown.cta_patterns.cta_performance
                                .length > 0 && (
                                <TableContainer sx={{ mt: 2 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>CTA Type</TableCell>
                                        <TableCell align="right">
                                          Posts
                                        </TableCell>
                                        <TableCell align="right">
                                          Avg Engagement
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {contentBreakdown.cta_patterns.cta_performance
                                        .slice(0, 10)
                                        .map((cta, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>
                                              {cta.cta_type
                                                .replace(/_/g, ' ')
                                                .toUpperCase()}
                                            </TableCell>
                                            <TableCell align="right">
                                              {cta.posts_count}
                                            </TableCell>
                                            <TableCell align="right">
                                              {cta.avg_engagement?.toFixed(0) ||
                                                0}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              )}
                          </CardContent>
                        </Card>
                      )}

                      {/* Posting Patterns */}
                      {contentBreakdown.posting_patterns && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Posting Time Patterns
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                {contentBreakdown.posting_patterns
                                  .top_day_of_week && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Best Day:{' '}
                                    <strong>
                                      {
                                        contentBreakdown.posting_patterns
                                          .top_day_of_week
                                      }
                                    </strong>
                                  </Typography>
                                )}
                              </Grid>
                              <Grid item xs={12} md={4}>
                                {contentBreakdown.posting_patterns.top_hour !==
                                  null && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Best Hour:{' '}
                                    <strong>
                                      {
                                        contentBreakdown.posting_patterns
                                          .top_hour
                                      }
                                      :00
                                    </strong>
                                  </Typography>
                                )}
                              </Grid>
                              <Grid item xs={12} md={4}>
                                {contentBreakdown.posting_patterns
                                  .top_time_category && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Best Time:{' '}
                                    <strong>
                                      {contentBreakdown.posting_patterns.top_time_category
                                        .replace(/_/g, ' ')
                                        .toUpperCase()}
                                    </strong>
                                  </Typography>
                                )}
                              </Grid>
                            </Grid>
                            {contentBreakdown.posting_patterns.insights &&
                              contentBreakdown.posting_patterns.insights
                                .length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <List dense>
                                    {contentBreakdown.posting_patterns.insights.map(
                                      (insight, idx) => (
                                        <ListItem key={idx}>
                                          <ListItemText primary={insight} />
                                        </ListItem>
                                      )
                                    )}
                                  </List>
                                </Box>
                              )}
                          </CardContent>
                        </Card>
                      )}

                      {/* Engagement Triggers */}
                      {contentBreakdown.engagement_triggers && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Engagement Trigger Patterns
                            </Typography>
                            {contentBreakdown.engagement_triggers
                              .top_engagement_trigger && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                              >
                                Top Trigger:{' '}
                                <strong>
                                  {contentBreakdown.engagement_triggers.top_engagement_trigger
                                    .replace(/_/g, ' ')
                                    .toUpperCase()}
                                </strong>
                              </Typography>
                            )}
                            {contentBreakdown.engagement_triggers
                              .trigger_performance &&
                              contentBreakdown.engagement_triggers
                                .trigger_performance.length > 0 && (
                                <TableContainer sx={{ mt: 2 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Trigger Type</TableCell>
                                        <TableCell align="right">
                                          Avg Likes
                                        </TableCell>
                                        <TableCell align="right">
                                          Avg Comments
                                        </TableCell>
                                        <TableCell align="right">
                                          Avg Shares
                                        </TableCell>
                                        <TableCell align="right">
                                          Avg Engagement
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {contentBreakdown.engagement_triggers.trigger_performance
                                        .slice(0, 10)
                                        .map((trigger, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>
                                              {trigger.trigger
                                                .replace(/_/g, ' ')
                                                .toUpperCase()}
                                            </TableCell>
                                            <TableCell align="right">
                                              {trigger.avg_likes?.toFixed(0) ||
                                                0}
                                            </TableCell>
                                            <TableCell align="right">
                                              {trigger.avg_comments?.toFixed(
                                                0
                                              ) || 0}
                                            </TableCell>
                                            <TableCell align="right">
                                              {trigger.avg_shares?.toFixed(0) ||
                                                0}
                                            </TableCell>
                                            <TableCell align="right">
                                              {trigger.avg_engagement?.toFixed(
                                                0
                                              ) || 0}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              )}
                          </CardContent>
                        </Card>
                      )}
                    </>
                  ) : (
                    <Alert severity="warning">
                      No posts available for content breakdown analysis. Run
                      scraping first to collect posts.
                    </Alert>
                  )}
                </Box>
              )}

              {/* Content Insights Tab */}
              {activeTab === 4 && contentInsights && (
                <Box>
                  <MlSectionTitle basis="content_insights">
                    AI-Friendly Content Insights & Recommendations
                  </MlSectionTitle>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Actionable insights for content creation based on
                    top-performing patterns across all competitors. Use these
                    recommendations to guide your content strategy.
                  </Alert>

                  {contentInsights.total_posts_analyzed > 0 ? (
                    <>
                      {/* Visual Preferences */}
                      {contentInsights.visual_preferences && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Visual Content Preferences
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Primary Visual Type:{' '}
                              <strong>
                                {contentInsights.visual_preferences.primary_visual_type
                                  ?.replace(/_/g, ' ')
                                  .toUpperCase() || 'N/A'}
                              </strong>
                            </Typography>
                            {contentInsights.visual_preferences
                              .recommendation && (
                              <Alert severity="info" sx={{ mt: 2 }}>
                                {
                                  contentInsights.visual_preferences
                                    .recommendation
                                }
                              </Alert>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {/* Caption Best Practices */}
                      {contentInsights.caption_best_practices && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Caption Best Practices
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Optimal Length:{' '}
                                  {
                                    contentInsights.caption_best_practices
                                      .optimal_length
                                  }{' '}
                                  characters
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Best Structure:{' '}
                                  {contentInsights.caption_best_practices.best_structure
                                    ?.replace(/_/g, ' ')
                                    .toUpperCase() || 'N/A'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Best Tone:{' '}
                                  {contentInsights.caption_best_practices.best_tone?.toUpperCase() ||
                                    'N/A'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Emoji Usage:{' '}
                                  {contentInsights.caption_best_practices.emoji_usage?.toFixed(
                                    1
                                  )}{' '}
                                  emojis per post
                                </Typography>
                              </Grid>
                            </Grid>
                            {contentInsights.caption_best_practices
                              .recommendation && (
                              <Alert severity="info" sx={{ mt: 2 }}>
                                {
                                  contentInsights.caption_best_practices
                                    .recommendation
                                }
                              </Alert>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {/* Hashtag Strategies */}
                      {contentInsights.hashtag_strategies && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Hashtag Strategies
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Optimal Count:{' '}
                              {contentInsights.hashtag_strategies.optimal_count?.toFixed(
                                0
                              )}{' '}
                              hashtags per post
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Placement:{' '}
                              {contentInsights.hashtag_strategies.placement
                                ?.replace(/_/g, ' ')
                                .toUpperCase() || 'N/A'}
                            </Typography>
                            {contentInsights.hashtag_strategies
                              .recommendation && (
                              <Alert severity="info" sx={{ mt: 2 }}>
                                {
                                  contentInsights.hashtag_strategies
                                    .recommendation
                                }
                              </Alert>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {/* Topic Priorities */}
                      {contentInsights.topic_priorities && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Content Topic Priorities
                            </Typography>
                            {contentInsights.topic_priorities
                              .top_performing_topic && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                              >
                                Top Performing Topic:{' '}
                                <strong>
                                  {contentInsights.topic_priorities.top_performing_topic.topic
                                    ?.replace(/_/g, ' ')
                                    .toUpperCase()}
                                </strong>
                                (Avg Engagement:{' '}
                                {contentInsights.topic_priorities.top_performing_topic.avg_engagement?.toFixed(
                                  0
                                ) || 0}
                                )
                              </Typography>
                            )}
                            {contentInsights.topic_priorities
                              .recommendation && (
                              <Alert severity="info" sx={{ mt: 2 }}>
                                {
                                  contentInsights.topic_priorities
                                    .recommendation
                                }
                              </Alert>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {/* CTA Strategies */}
                      {contentInsights.cta_strategies && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              CTA Strategies
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Usage:{' '}
                              {contentInsights.cta_strategies.usage_percentage?.toFixed(
                                1
                              )}
                              % of posts include CTAs
                            </Typography>
                            {contentInsights.cta_strategies.best_cta_type && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                              >
                                Best CTA Type:{' '}
                                <strong>
                                  {contentInsights.cta_strategies.best_cta_type
                                    .replace(/_/g, ' ')
                                    .toUpperCase()}
                                </strong>
                              </Typography>
                            )}
                            {contentInsights.cta_strategies.recommendation && (
                              <Alert severity="info" sx={{ mt: 2 }}>
                                {contentInsights.cta_strategies.recommendation}
                              </Alert>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {/* Posting Recommendations */}
                      {contentInsights.posting_recommendations && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Posting Time Recommendations
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Best Day:{' '}
                              {contentInsights.posting_recommendations
                                .best_day || 'N/A'}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Best Hour:{' '}
                              {contentInsights.posting_recommendations
                                .best_hour !== null
                                ? `${contentInsights.posting_recommendations.best_hour}:00`
                                : 'N/A'}
                            </Typography>
                            {contentInsights.posting_recommendations
                              .recommendation && (
                              <Alert severity="info" sx={{ mt: 2 }}>
                                {
                                  contentInsights.posting_recommendations
                                    .recommendation
                                }
                              </Alert>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {/* Engagement Strategies */}
                      {contentInsights.engagement_strategies && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Engagement Strategies
                            </Typography>
                            {contentInsights.engagement_strategies
                              .best_trigger && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                              >
                                Best Engagement Trigger:{' '}
                                <strong>
                                  {contentInsights.engagement_strategies.best_trigger
                                    .replace(/_/g, ' ')
                                    .toUpperCase()}
                                </strong>
                              </Typography>
                            )}
                            {contentInsights.engagement_strategies
                              .recommendation && (
                              <Alert severity="info" sx={{ mt: 2 }}>
                                {
                                  contentInsights.engagement_strategies
                                    .recommendation
                                }
                              </Alert>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {/* AI Content Templates */}
                      {contentInsights.ai_content_templates &&
                        contentInsights.ai_content_templates.length > 0 && (
                          <Card sx={{ mb: 3 }}>
                            <CardContent>
                              <Typography variant="subtitle1" gutterBottom>
                                AI Content Generation Templates
                              </Typography>
                              <List>
                                {contentInsights.ai_content_templates.map(
                                  (template, idx) => (
                                    <ListItem key={idx}>
                                      <ListItemIcon>
                                        <CreateIcon color="primary" />
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={`${
                                          template.element?.toUpperCase() ||
                                          'CONTENT'
                                        }: ${
                                          template.type
                                            ?.replace(/_/g, ' ')
                                            .toUpperCase() ||
                                          template.structure
                                            ?.replace(/_/g, ' ')
                                            .toUpperCase() ||
                                          'N/A'
                                        }`}
                                        secondary={template.recommendation}
                                      />
                                    </ListItem>
                                  )
                                )}
                              </List>
                            </CardContent>
                          </Card>
                        )}
                    </>
                  ) : (
                    <Alert severity="warning">
                      No posts available for content insights. Run scraping
                      first to collect posts, then trigger content breakdown
                      analysis.
                    </Alert>
                  )}
                </Box>
              )}

              {/* SEO Analysis Tab - Website SEO */}
              {activeTab === 5 && seoAnalysis && (
                <Box>
                  <MlSectionTitle basis="seo_rubric" sx={{ mb: 3 }}>
                    Website SEO Analysis
                  </MlSectionTitle>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    SEO analysis of competitor websites (not social media
                    posts). This analyzes on-page SEO, technical SEO, keywords,
                    and content structure.
                  </Alert>

                  {/* SEO Score Card */}
                  <Card
                    sx={{
                      mb: 3,
                      bgcolor:
                        seoAnalysis.avg_seo_score >= 0.7
                          ? 'success.light'
                          : seoAnalysis.avg_seo_score >= 0.5
                          ? 'warning.light'
                          : 'error.light',
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography
                              variant="h3"
                              sx={{ fontWeight: 'bold', color: 'primary.main' }}
                            >
                              {(seoAnalysis.avg_seo_score * 100).toFixed(0)}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Average SEO Score
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4">
                              {seoAnalysis.total_pages || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Pages Analyzed
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4">
                              {seoAnalysis.keyword_analysis
                                ?.total_unique_keywords || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Unique Keywords
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* On-Page SEO */}
                  {seoAnalysis.on_page_seo && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          On-Page SEO
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              On-Page Score
                            </Typography>
                            <Typography variant="h5">
                              {(
                                seoAnalysis.on_page_seo.avg_score * 100
                              ).toFixed(0)}
                              %
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Pages with Title Tags
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.on_page_seo.pages_with_title_pct?.toFixed(
                                1
                              ) || 0}
                              %
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Pages with Meta Descriptions
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.on_page_seo.pages_with_meta_pct?.toFixed(
                                1
                              ) || 0}
                              %
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Pages with H1 Tags
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.on_page_seo.pages_with_h1_pct?.toFixed(
                                1
                              ) || 0}
                              %
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Technical SEO */}
                  {seoAnalysis.technical_seo && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Technical SEO
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Technical Score
                            </Typography>
                            <Typography variant="h5">
                              {(
                                seoAnalysis.technical_seo.avg_score * 100
                              ).toFixed(0)}
                              %
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Pages with HTTPS
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.technical_seo.pages_with_ssl_pct?.toFixed(
                                1
                              ) || 0}
                              %
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Mobile-Friendly Pages
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.technical_seo.pages_mobile_friendly_pct?.toFixed(
                                1
                              ) || 0}
                              %
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Avg Load Time
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.technical_seo.avg_load_time?.toFixed(
                                2
                              ) || 0}
                              s
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Keyword Analysis */}
                  {seoAnalysis.keyword_analysis && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Keyword Analysis
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={4}>
                            <Typography variant="body2" color="text.secondary">
                              Total Unique Keywords
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.keyword_analysis
                                .total_unique_keywords || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="body2" color="text.secondary">
                              Average Keywords per Page
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.keyword_analysis.avg_keyword_count?.toFixed(
                                1
                              ) || 0}
                            </Typography>
                          </Grid>
                        </Grid>

                        {seoAnalysis.top_keywords &&
                          seoAnalysis.top_keywords.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                Top Keywords
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                }}
                              >
                                {seoAnalysis.top_keywords
                                  .slice(0, 20)
                                  .map((keyword, idx) => (
                                    <Chip
                                      key={idx}
                                      label={keyword}
                                      size="small"
                                    />
                                  ))}
                              </Box>
                            </Box>
                          )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Content Analysis */}
                  {seoAnalysis.content_analysis && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Content Quality
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Content Score
                            </Typography>
                            <Typography variant="h5">
                              {(
                                seoAnalysis.content_analysis.avg_score * 100
                              ).toFixed(0)}
                              %
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Avg Word Count
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.content_analysis.avg_word_count?.toFixed(
                                0
                              ) || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Avg Headings
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.content_analysis.avg_heading_count?.toFixed(
                                1
                              ) || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Avg Paragraphs
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.content_analysis.avg_paragraph_count?.toFixed(
                                1
                              ) || 0}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Link Analysis */}
                  {seoAnalysis.link_analysis && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Link Structure
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Total Internal Links
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.link_analysis.total_internal_links ||
                                0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Total External Links
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.link_analysis.total_external_links ||
                                0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Pages with Links
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.link_analysis.pages_with_links_pct?.toFixed(
                                1
                              ) || 0}
                              %
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Avg Links per Page
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.link_analysis.avg_links_per_page?.toFixed(
                                1
                              ) || 0}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Image SEO */}
                  {seoAnalysis.image_seo && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Image SEO
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Total Images
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.image_seo.total_images || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Images with Alt Text
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.image_seo.images_with_alt || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Images without Alt Text
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.image_seo.images_without_alt || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              Alt Text Coverage
                            </Typography>
                            <Typography variant="h5">
                              {seoAnalysis.image_seo.alt_text_coverage?.toFixed(
                                1
                              ) || 0}
                              %
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Detailed Link Breakdown */}
                  {seoAnalysis.detailed_link_breakdown &&
                    Object.keys(seoAnalysis.detailed_link_breakdown).length >
                      0 && (
                      <Card sx={{ mb: 3 }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Link Categorization Breakdown
                          </Typography>
                          <Grid container spacing={3}>
                            {Object.entries(
                              seoAnalysis.detailed_link_breakdown
                            ).map(([category, data]) => {
                              if (!data || data.count === 0) return null;

                              const categoryLabels = {
                                local_seo: 'Local SEO Citations',
                                review_sites: 'Review Sites',
                                industry_directories: 'Industry Directories',
                                content_pr: 'Content & PR',
                                high_authority: 'High Authority Links',
                                social_signals: 'Social Signals',
                              };

                              return (
                                <Grid item xs={12} md={6} key={category}>
                                  <Card variant="outlined">
                                    <CardContent>
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight="bold"
                                        gutterBottom
                                      >
                                        {categoryLabels[category] ||
                                          category
                                            .replace('_', ' ')
                                            .replace(/\b\w/g, (l) =>
                                              l.toUpperCase()
                                            )}
                                      </Typography>
                                      <Typography variant="h5" color="primary">
                                        {data.count}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 1 }}
                                      >
                                        Links across {data.domains?.length || 0}{' '}
                                        platforms
                                      </Typography>
                                      {data.insights &&
                                        data.insights.length > 0 && (
                                          <Alert severity="info" sx={{ mt: 1 }}>
                                            {data.insights[0]}
                                          </Alert>
                                        )}
                                      {data.domains &&
                                        data.domains.length > 0 && (
                                          <Box sx={{ mt: 2 }}>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              Top Platforms:
                                            </Typography>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.5,
                                                mt: 0.5,
                                              }}
                                            >
                                              {data.domains
                                                .slice(0, 5)
                                                .map((domain, idx) => (
                                                  <Chip
                                                    key={idx}
                                                    label={domain.domain}
                                                    size="small"
                                                    variant="outlined"
                                                  />
                                                ))}
                                            </Box>
                                          </Box>
                                        )}
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </CardContent>
                      </Card>
                    )}

                  {/* Keyword Opportunities */}
                  {seoAnalysis.keyword_opportunities && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Keyword Opportunities
                        </Typography>

                        {/* Primary Keywords */}
                        {seoAnalysis.keyword_opportunities.primary_keywords &&
                          seoAnalysis.keyword_opportunities.primary_keywords
                            .length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <TrendingUpIcon color="primary" />
                                Primary Keywords (High Value)
                              </Typography>
                              <Grid container spacing={2}>
                                {seoAnalysis.keyword_opportunities.primary_keywords
                                  .slice(0, 10)
                                  .map((kw, idx) => (
                                    <Grid item xs={12} sm={6} md={4} key={idx}>
                                      <Card variant="outlined">
                                        <CardContent
                                          sx={{
                                            p: 2,
                                            '&:last-child': { pb: 2 },
                                          }}
                                        >
                                          <Typography
                                            variant="body1"
                                            fontWeight="bold"
                                          >
                                            {kw.keyword}
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              gap: 1,
                                              mt: 1,
                                              flexWrap: 'wrap',
                                            }}
                                          >
                                            <Chip
                                              label={`${kw.competitor_count} competitors`}
                                              size="small"
                                              color="primary"
                                              variant="outlined"
                                            />
                                            <Chip
                                              label={kw.estimated_difficulty}
                                              size="small"
                                              color={
                                                kw.estimated_difficulty ===
                                                'high'
                                                  ? 'error'
                                                  : 'default'
                                              }
                                            />
                                            <Chip
                                              label={kw.search_intent}
                                              size="small"
                                              variant="outlined"
                                            />
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  ))}
                              </Grid>
                            </Box>
                          )}

                        {/* Long-Tail Keywords */}
                        {seoAnalysis.keyword_opportunities.long_tail_keywords &&
                          seoAnalysis.keyword_opportunities.long_tail_keywords
                            .length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <RecommendIcon color="success" />
                                Long-Tail Opportunities (Quick Wins)
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                }}
                              >
                                {seoAnalysis.keyword_opportunities.long_tail_keywords
                                  .slice(0, 20)
                                  .map((kw, idx) => (
                                    <Chip
                                      key={idx}
                                      label={kw.keyword}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      title={`Content Type: ${
                                        kw.content_suggestion || 'blog_post'
                                      }`}
                                    />
                                  ))}
                              </Box>
                            </Box>
                          )}

                        {/* Content Clusters */}
                        {seoAnalysis.keyword_opportunities.content_clusters &&
                          seoAnalysis.keyword_opportunities.content_clusters
                            .length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                Content Topic Clusters
                              </Typography>
                              <Grid container spacing={2}>
                                {seoAnalysis.keyword_opportunities.content_clusters.map(
                                  (cluster, idx) => (
                                    <Grid item xs={12} md={6} key={idx}>
                                      <Card variant="outlined">
                                        <CardContent>
                                          <Typography
                                            variant="subtitle2"
                                            fontWeight="bold"
                                            gutterBottom
                                          >
                                            {cluster.theme}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mb: 1 }}
                                          >
                                            {cluster.keyword_count} related
                                            keywords
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              gap: 0.5,
                                            }}
                                          >
                                            {cluster.keywords
                                              ?.slice(0, 5)
                                              .map((kw, kwIdx) => (
                                                <Chip
                                                  key={kwIdx}
                                                  label={kw.keyword}
                                                  size="small"
                                                  variant="outlined"
                                                />
                                              ))}
                                          </Box>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mt: 1, display: 'block' }}
                                          >
                                            Suggested:{' '}
                                            {cluster.content_suggestion ||
                                              'blog_post'}
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  )
                                )}
                              </Grid>
                            </Box>
                          )}

                        {/* Keyword Gaps (if client data available) */}
                        {seoAnalysis.keyword_opportunities.keyword_gaps &&
                          seoAnalysis.keyword_opportunities.keyword_gaps
                            .length > 0 && (
                            <Box>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <AssessmentIcon color="warning" />
                                Keyword Gaps (Competitors Rank, You Don't)
                              </Typography>
                              <Alert severity="warning" sx={{ mb: 2 }}>
                                {
                                  seoAnalysis.keyword_opportunities.keyword_gaps
                                    .length
                                }{' '}
                                high-value keywords competitors use that you
                                don't
                              </Alert>
                              <Grid container spacing={2}>
                                {seoAnalysis.keyword_opportunities.keyword_gaps
                                  .slice(0, 10)
                                  .map((gap, idx) => (
                                    <Grid item xs={12} sm={6} key={idx}>
                                      <Card
                                        variant="outlined"
                                        sx={{ bgcolor: 'warning.light' }}
                                      >
                                        <CardContent
                                          sx={{
                                            p: 2,
                                            '&:last-child': { pb: 2 },
                                          }}
                                        >
                                          <Typography
                                            variant="body1"
                                            fontWeight="bold"
                                          >
                                            {gap.keyword}
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              gap: 1,
                                              mt: 1,
                                            }}
                                          >
                                            <Chip
                                              label={`${gap.competitor_count} competitors`}
                                              size="small"
                                              color="warning"
                                            />
                                            <Chip
                                              label={`Score: ${gap.opportunity_score}`}
                                              size="small"
                                              variant="outlined"
                                            />
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  ))}
                              </Grid>
                            </Box>
                          )}
                      </CardContent>
                    </Card>
                  )}

                  {/* SEO Recommendations */}
                  {seoAnalysis.seo_recommendations &&
                    seoAnalysis.seo_recommendations.length > 0 && (
                      <Card sx={{ mb: 3 }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            SEO Recommendations
                          </Typography>
                          <Stack spacing={2}>
                            {seoAnalysis.seo_recommendations.map((rec, idx) => (
                              <Alert
                                key={idx}
                                severity={
                                  rec.priority === 'high'
                                    ? 'error'
                                    : rec.priority === 'medium'
                                    ? 'warning'
                                    : 'info'
                                }
                              >
                                <Typography variant="subtitle2" gutterBottom>
                                  {rec.category}:{' '}
                                  {rec.issue || rec.recommendation}
                                </Typography>
                                <Typography variant="body2">
                                  {rec.recommendation || rec.action}
                                </Typography>
                              </Alert>
                            ))}
                          </Stack>
                        </CardContent>
                      </Card>
                    )}

                  {(!seoAnalysis.total_pages ||
                    seoAnalysis.total_pages === 0) && (
                    <Alert severity="warning">
                      No website SEO data available. Run website scraping and
                      SEO analysis to analyze competitor websites.
                    </Alert>
                  )}
                </Box>
              )}

              {/* Gap Analysis Tab */}
              {activeTab === 6 && gapAnalysis && (
                <Box>
                  <MlSectionTitle basis="gap_projection" sx={{ mb: 3 }}>
                    SEO Gap Analysis: Client vs Competitors
                  </MlSectionTitle>

                  {gapAnalysis.error ? (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      {gapAnalysis.message || gapAnalysis.error}
                    </Alert>
                  ) : (
                    <>
                      {/* Score Comparison */}
                      <Card
                        sx={{
                          mb: 3,
                          bgcolor:
                            gapAnalysis.score_difference > 0
                              ? 'error.light'
                              : 'success.light',
                        }}
                      >
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Overall SEO Score Comparison
                          </Typography>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="h4"
                                  sx={{
                                    fontWeight: 'bold',
                                    color: 'primary.main',
                                  }}
                                >
                                  {(gapAnalysis.client_score * 100).toFixed(0)}%
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Your Website Score
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="h4"
                                  sx={{
                                    fontWeight: 'bold',
                                    color: 'text.secondary',
                                  }}
                                >
                                  {(
                                    gapAnalysis.competitor_avg_score * 100
                                  ).toFixed(0)}
                                  %
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Competitor Average
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="h4"
                                  sx={{
                                    fontWeight: 'bold',
                                    color:
                                      gapAnalysis.score_difference > 0
                                        ? 'error.main'
                                        : 'success.main',
                                  }}
                                >
                                  {gapAnalysis.score_difference > 0 ? '+' : ''}
                                  {(gapAnalysis.score_difference * 100).toFixed(
                                    0
                                  )}
                                  %
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Gap to Close
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>

                      {/* Category Gaps */}
                      <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                On-Page SEO Gap
                              </Typography>
                              <Box sx={{ mb: 2 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                  }}
                                >
                                  <Typography variant="body2">
                                    Your Score
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {(
                                      (gapAnalysis.on_page_gaps?.client_score ||
                                        0) * 100
                                    ).toFixed(0)}
                                    %
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                  }}
                                >
                                  <Typography variant="body2">
                                    Competitor Avg
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {(
                                      (gapAnalysis.on_page_gaps
                                        ?.competitor_avg_score || 0) * 100
                                    ).toFixed(0)}
                                    %
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={
                                    (gapAnalysis.on_page_gaps?.client_score ||
                                      0) * 100
                                  }
                                  sx={{ height: 8, borderRadius: 1 }}
                                />
                              </Box>
                              {gapAnalysis.on_page_gaps?.gaps?.length > 0 && (
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  fullWidth
                                  sx={{ mt: 2 }}
                                  onClick={() => {
                                    setGapModalData({
                                      title: 'On-Page SEO Issues',
                                      gaps: gapAnalysis.on_page_gaps.gaps,
                                      clientScore:
                                        gapAnalysis.on_page_gaps.client_score,
                                      competitorScore:
                                        gapAnalysis.on_page_gaps
                                          .competitor_avg_score,
                                    });
                                    setGapModalOpen(true);
                                  }}
                                  startIcon={<InfoIcon />}
                                >
                                  {gapAnalysis.on_page_gaps.gaps.length}{' '}
                                  issue(s) identified
                                </Button>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                Technical SEO Gap
                              </Typography>
                              <Box sx={{ mb: 2 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                  }}
                                >
                                  <Typography variant="body2">
                                    Your Score
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {(
                                      (gapAnalysis.technical_gaps
                                        ?.client_score || 0) * 100
                                    ).toFixed(0)}
                                    %
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                  }}
                                >
                                  <Typography variant="body2">
                                    Competitor Avg
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {(
                                      (gapAnalysis.technical_gaps
                                        ?.competitor_avg_score || 0) * 100
                                    ).toFixed(0)}
                                    %
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={
                                    (gapAnalysis.technical_gaps?.client_score ||
                                      0) * 100
                                  }
                                  sx={{ height: 8, borderRadius: 1 }}
                                />
                              </Box>
                              {gapAnalysis.technical_gaps?.gaps?.length > 0 && (
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  fullWidth
                                  sx={{ mt: 2 }}
                                  onClick={() => {
                                    setGapModalData({
                                      title: 'Technical SEO Issues',
                                      gaps: gapAnalysis.technical_gaps.gaps,
                                      clientScore:
                                        gapAnalysis.technical_gaps.client_score,
                                      competitorScore:
                                        gapAnalysis.technical_gaps
                                          .competitor_avg_score,
                                    });
                                    setGapModalOpen(true);
                                  }}
                                  startIcon={<InfoIcon />}
                                >
                                  {gapAnalysis.technical_gaps.gaps.length}{' '}
                                  issue(s) identified
                                </Button>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                Content Quality Gap
                              </Typography>
                              <Box sx={{ mb: 2 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                  }}
                                >
                                  <Typography variant="body2">
                                    Your Word Count
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {gapAnalysis.content_gaps?.client_word_count?.toLocaleString() ||
                                      0}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                  }}
                                >
                                  <Typography variant="body2">
                                    Competitor Avg
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {gapAnalysis.content_gaps?.competitor_avg_words?.toLocaleString() ||
                                      0}
                                  </Typography>
                                </Box>
                                {gapAnalysis.content_gaps?.word_count_gap >
                                  0 && (
                                  <Button
                                    variant="outlined"
                                    color="info"
                                    fullWidth
                                    sx={{ mt: 1 }}
                                    onClick={() => {
                                      setGapModalData({
                                        title: 'Content Quality Issues',
                                        gaps:
                                          gapAnalysis.content_gaps?.gaps || [],
                                        wordCountGap:
                                          gapAnalysis.content_gaps
                                            .word_count_gap,
                                        clientWordCount:
                                          gapAnalysis.content_gaps
                                            .client_word_count,
                                        competitorWordCount:
                                          gapAnalysis.content_gaps
                                            .competitor_avg_words,
                                      });
                                      setGapModalOpen(true);
                                    }}
                                    startIcon={<InfoIcon />}
                                  >
                                    Need{' '}
                                    {gapAnalysis.content_gaps.word_count_gap.toLocaleString()}{' '}
                                    more words
                                  </Button>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                Link Building Gap
                              </Typography>
                              <Box sx={{ mb: 2 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                  }}
                                >
                                  <Typography variant="body2">
                                    Your External Links
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {gapAnalysis.link_gaps
                                      ?.client_external_links || 0}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                  }}
                                >
                                  <Typography variant="body2">
                                    Competitor Avg
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {Math.round(
                                      gapAnalysis.link_gaps
                                        ?.competitor_avg_external || 0
                                    )}
                                  </Typography>
                                </Box>
                                {gapAnalysis.link_gaps?.gaps?.length > 0 && (
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    fullWidth
                                    sx={{ mt: 1 }}
                                    onClick={() => {
                                      setGapModalData({
                                        title: 'Link Building Issues',
                                        gaps: gapAnalysis.link_gaps.gaps,
                                        clientLinks:
                                          gapAnalysis.link_gaps
                                            .client_external_links,
                                        competitorLinks:
                                          gapAnalysis.link_gaps
                                            .competitor_avg_external,
                                      });
                                      setGapModalOpen(true);
                                    }}
                                    startIcon={<InfoIcon />}
                                  >
                                    {gapAnalysis.link_gaps.gaps[0]?.action ||
                                      'Build more backlinks'}
                                  </Button>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      {/* Quick Wins */}
                      {gapAnalysis.quick_wins &&
                        gapAnalysis.quick_wins.length > 0 && (
                          <Card sx={{ mb: 3 }}>
                            <CardContent>
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <TrendingUpIcon color="success" />
                                Quick Wins (Low Effort, High Impact)
                              </Typography>
                              <Stack spacing={2}>
                                {gapAnalysis.quick_wins.map((rec, idx) => (
                                  <Card
                                    key={idx}
                                    variant="outlined"
                                    sx={{ bgcolor: 'success.light' }}
                                  >
                                    <CardContent>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight="bold"
                                        >
                                          {rec.issue}
                                        </Typography>
                                        <Chip
                                          label={rec.impact}
                                          size="small"
                                          color="success"
                                          sx={{ textTransform: 'capitalize' }}
                                        />
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        sx={{ mb: 1 }}
                                      >
                                        <strong>Current:</strong>{' '}
                                        {rec.current_state}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ mb: 1 }}
                                      >
                                        <strong>Target:</strong>{' '}
                                        {rec.target_state}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="primary"
                                        fontWeight="medium"
                                      >
                                        ✓ {rec.action}
                                      </Typography>
                                      {rec.expected_improvement && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ mt: 1, display: 'block' }}
                                        >
                                          Expected: {rec.expected_improvement}
                                        </Typography>
                                      )}
                                    </CardContent>
                                  </Card>
                                ))}
                              </Stack>
                            </CardContent>
                          </Card>
                        )}

                      {/* High Impact Recommendations */}
                      {gapAnalysis.high_impact &&
                        gapAnalysis.high_impact.length > 0 && (
                          <Card sx={{ mb: 3 }}>
                            <CardContent>
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <AssessmentIcon color="error" />
                                High Impact Recommendations
                              </Typography>
                              <Stack spacing={2}>
                                {gapAnalysis.high_impact.map((rec, idx) => (
                                  <Card key={idx} variant="outlined">
                                    <CardContent>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight="bold"
                                        >
                                          {rec.issue}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                          <Chip
                                            label={rec.impact}
                                            size="small"
                                            color="error"
                                            sx={{ textTransform: 'capitalize' }}
                                          />
                                          <Chip
                                            label={`${rec.estimated_effort_hours}h`}
                                            size="small"
                                            variant="outlined"
                                          />
                                        </Box>
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        sx={{ mb: 1 }}
                                      >
                                        <strong>Current:</strong>{' '}
                                        {rec.current_state}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ mb: 1 }}
                                      >
                                        <strong>Target:</strong>{' '}
                                        {rec.target_state}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="primary"
                                        fontWeight="medium"
                                      >
                                        → {rec.action}
                                      </Typography>
                                      {rec.expected_improvement && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ mt: 1, display: 'block' }}
                                        >
                                          Expected: {rec.expected_improvement}
                                        </Typography>
                                      )}
                                    </CardContent>
                                  </Card>
                                ))}
                              </Stack>
                            </CardContent>
                          </Card>
                        )}

                      {/* Impact Analysis */}
                      {gapAnalysis.impact_analysis && (
                        <Card sx={{ mb: 3 }}>
                          <CardContent>
                            <MlSectionTitle basis="gap_projection">
                              Expected Impact
                            </MlSectionTitle>
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography
                                    variant="h4"
                                    color="success.main"
                                    fontWeight="bold"
                                  >
                                    +
                                    {(
                                      gapAnalysis.impact_analysis
                                        .estimated_score_improvement * 100
                                    ).toFixed(0)}
                                    %
                                  </Typography>
                                  <MlMetricLabel basis="gap_projection">
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Estimated Score Improvement
                                    </Typography>
                                  </MlMetricLabel>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography
                                    variant="h4"
                                    color="primary.main"
                                    fontWeight="bold"
                                  >
                                    {(
                                      gapAnalysis.impact_analysis
                                        .estimated_new_score * 100
                                    ).toFixed(0)}
                                    %
                                  </Typography>
                                  <MlMetricLabel basis="gap_projection">
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Projected New Score
                                    </Typography>
                                  </MlMetricLabel>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h4" fontWeight="bold">
                                    {gapAnalysis.impact_analysis
                                      .time_to_implement_quick_wins || 0}
                                    h
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Quick Wins Time Estimate
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      )}

                      {/* Link Building Strategy */}
                      {gapAnalysis.link_building_strategy &&
                        Object.keys(gapAnalysis.link_building_strategy).length >
                          0 && (
                          <Card>
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                Link Building Strategy (Based on Competitor
                                Patterns)
                              </Typography>

                              {/* Detailed Link Breakdown */}
                              {gapAnalysis.link_building_strategy
                                .detailed_breakdown &&
                                Object.keys(
                                  gapAnalysis.link_building_strategy
                                    .detailed_breakdown
                                ).length > 0 && (
                                  <Box sx={{ mb: 3 }}>
                                    <Typography
                                      variant="subtitle1"
                                      gutterBottom
                                    >
                                      Competitor Link Distribution
                                    </Typography>
                                    <Grid container spacing={2}>
                                      {Object.entries(
                                        gapAnalysis.link_building_strategy
                                          .detailed_breakdown
                                      ).map(([category, data]) => {
                                        if (!data || data.count === 0)
                                          return null;

                                        const categoryLabels = {
                                          local_seo: 'Local SEO Citations',
                                          review_sites: 'Review Sites',
                                          industry_directories:
                                            'Industry Directories',
                                          content_pr: 'Content & PR',
                                          high_authority: 'High Authority',
                                          social_signals: 'Social Signals',
                                        };

                                        return (
                                          <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            key={category}
                                          >
                                            <Card variant="outlined">
                                              <CardContent
                                                sx={{
                                                  p: 2,
                                                  '&:last-child': { pb: 2 },
                                                }}
                                              >
                                                <Typography
                                                  variant="subtitle2"
                                                  fontWeight="bold"
                                                >
                                                  {categoryLabels[category] ||
                                                    category}
                                                </Typography>
                                                <Typography
                                                  variant="h6"
                                                  color="primary"
                                                >
                                                  {data.count}
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  {data.domains?.length || 0}{' '}
                                                  platforms
                                                </Typography>
                                                {data.insights &&
                                                  data.insights.length > 0 && (
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                      sx={{
                                                        mt: 1,
                                                        display: 'block',
                                                      }}
                                                    >
                                                      {data.insights[0]}
                                                    </Typography>
                                                  )}
                                              </CardContent>
                                            </Card>
                                          </Grid>
                                        );
                                      })}
                                    </Grid>
                                  </Box>
                                )}

                              {gapAnalysis.link_building_strategy
                                .priority_categories && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Priority Categories:
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ flexWrap: 'wrap' }}
                                  >
                                    {gapAnalysis.link_building_strategy.priority_categories.map(
                                      (cat, idx) => (
                                        <Chip
                                          key={idx}
                                          label={cat.replace('_', ' ')}
                                          size="small"
                                          color="primary"
                                        />
                                      )
                                    )}
                                  </Stack>
                                </Box>
                              )}
                              {gapAnalysis.link_building_strategy
                                .target_domains &&
                                gapAnalysis.link_building_strategy
                                  .target_domains.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                    >
                                      Target Domains (Linked by Multiple
                                      Competitors):
                                    </Typography>
                                    <List dense>
                                      {gapAnalysis.link_building_strategy.target_domains
                                        .slice(0, 10)
                                        .map((domain, idx) => (
                                          <ListItem key={idx}>
                                            <ListItemIcon>
                                              <NavigateNextIcon />
                                            </ListItemIcon>
                                            <ListItemText primary={domain} />
                                          </ListItem>
                                        ))}
                                    </List>
                                  </Box>
                                )}
                              {gapAnalysis.link_building_strategy
                                .category_suggestions && (
                                <Box>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Category-Specific Actions:
                                  </Typography>
                                  <Stack spacing={1}>
                                    {gapAnalysis.link_building_strategy.category_suggestions.map(
                                      (suggestion, idx) => (
                                        <Alert key={idx} severity="info">
                                          <Typography variant="body2">
                                            <strong>{suggestion.action}</strong>
                                          </Typography>
                                        </Alert>
                                      )
                                    )}
                                  </Stack>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        )}
                    </>
                  )}
                </Box>
              )}

              {/* Sentiment Tab */}
              {activeTab === 7 && sentimentAnalysis && (
                <Box>
                  <MlSectionTitle basis="sentiment_nlp">
                    Sentiment Analysis
                  </MlSectionTitle>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <MlSectionTitle basis="sentiment_nlp" variant="subtitle1">
                            Overall Sentiment Distribution
                          </MlSectionTitle>
                          {getSentimentPieData(sentimentAnalysis).length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie
                                  data={getSentimentPieData(sentimentAnalysis)}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }) =>
                                    `${name}: ${(percent * 100).toFixed(0)}%`
                                  }
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {getSentimentPieData(sentimentAnalysis).map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={entry.color}
                                    />
                                  ))}
                                </Pie>
                                <RechartsTooltip />
                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          ) : (
                            <Alert severity="info">
                              No sentiment data available
                            </Alert>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>
                            Sentiment Summary
                          </Typography>
                          <Stack spacing={2}>
                            <Box>
                              <MlMetricLabel basis="sentiment_nlp">
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Average Sentiment
                                </Typography>
                              </MlMetricLabel>
                              <Typography
                                variant="h5"
                                color={getSentimentColor(
                                  sentimentAnalysis.avg_sentiment || 0.5
                                )}
                              >
                                {getSentimentLabel(
                                  sentimentAnalysis.avg_sentiment || 0.5
                                )}
                              </Typography>
                              <Typography variant="caption">
                                {(sentimentAnalysis.avg_sentiment || 0.5) * 100}
                                % positive
                              </Typography>
                              {sentimentAnalysis.model_name && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  NLP model: {sentimentAnalysis.model_name}
                                </Typography>
                              )}
                            </Box>
                            <Divider />
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                              >
                                Post Breakdown
                              </Typography>
                              <Stack spacing={1}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Typography variant="body2">
                                    Positive:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color="success.main"
                                  >
                                    {sentimentAnalysis.positive_count || 0} (
                                    {sentimentAnalysis.positive_percentage?.toFixed(
                                      1
                                    ) || 0}
                                    %)
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Typography variant="body2">
                                    Negative:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color="error.main"
                                  >
                                    {sentimentAnalysis.negative_count || 0} (
                                    {sentimentAnalysis.negative_percentage?.toFixed(
                                      1
                                    ) || 0}
                                    %)
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Typography variant="body2">
                                    Neutral:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color="warning.main"
                                  >
                                    {sentimentAnalysis.neutral_count || 0} (
                                    {sentimentAnalysis.neutral_percentage?.toFixed(
                                      1
                                    ) || 0}
                                    %)
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Typography variant="body2">
                                    Total Posts:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {sentimentAnalysis.total_posts_analyzed ||
                                      0}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Competitor Sentiment Breakdown */}
                  {sentimentAnalysis.competitor_sentiments?.length > 0 && (
                    <Card sx={{ mt: 3 }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          Sentiment by Competitor
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Competitor</TableCell>
                                <TableCell>Week</TableCell>
                                <TableCell>Sentiment</TableCell>
                                <TableCell>Score</TableCell>
                                <TableCell>Posts</TableCell>
                                <TableCell>Model</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sentimentAnalysis.competitor_sentiments.map(
                                (comp) => (
                                  <TableRow
                                    key={comp.competitor_id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() =>
                                      handleCompetitorClick({
                                        competitor_id: comp.competitor_id,
                                        name: comp.name,
                                        brand_sentiment: comp.sentiment,
                                      })
                                    }
                                  >
                                    <TableCell>{comp.name}</TableCell>
                                    <TableCell>
                                      {comp.week_start || 'Current'}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={comp.sentiment_label}
                                        color={getSentimentColor(
                                          comp.sentiment
                                        )}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {(comp.sentiment * 100).toFixed(1)}%
                                    </TableCell>
                                    <TableCell>{comp.total_posts || 0}</TableCell>
                                    <TableCell>
                                      {comp.model_name ||
                                        sentimentAnalysis.model_name ||
                                        'NLP'}
                                    </TableCell>
                                  </TableRow>
                                )
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}

              {/* Platform Presence Tab */}
              {activeTab === 8 && platformPresence && (
                <Box>
                  <MlSectionTitle basis="platform_engagement_rate">
                    Platform Presence Analysis
                  </MlSectionTitle>

                  {getPlatformPresenceChartData(platformPresence).length > 0 && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Platform Usage & Performance
                            </Typography>
                            <ResponsiveContainer width="100%" height={400}>
                              <BarChart data={getPlatformPresenceChartData(platformPresence)}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="platform" />
                                <YAxis yAxisId="left" />
                                <YAxis yAxisId="right" orientation="right" />
                                <RechartsTooltip />
                                <Legend />
                                <Bar
                                  yAxisId="left"
                                  dataKey="competitors"
                                  fill="#8884d8"
                                  name="Competitors Using"
                                />
                                <Bar
                                  yAxisId="right"
                                  dataKey="engagementRate"
                                  fill="#00C49F"
                                  name="Engagement Rate (%)"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Platform Metrics
                            </Typography>
                            <Stack spacing={2}>
                              {getPlatformPresenceChartData(platformPresence).map(
                                (platform) => (
                                  <Box key={platform.platform}>
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                    >
                                      {platform.platform}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {platform.competitors} competitors |
                                      {platform.followers.toLocaleString()}{' '}
                                      followers |
                                      {platform.engagementRate.toFixed(1)}%
                                      engagement
                                    </Typography>
                                  </Box>
                                )
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              )}

              {/* Recommendations Tab */}
              {activeTab === 9 && (
                <Box>
                  {/* Task 3.4: Content Suggestions (top-performer / heuristics / LLM) */}
                  {selectedClient && (
                    <Box sx={{ mb: 3 }}>
                      <ContentSuggestionsPanel
                        clientId={selectedClient.id}
                        platform={null}
                      />
                    </Box>
                  )}

                  <MlSectionTitle
                    basis="rule_based"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}
                  >
                    <RecommendIcon /> Actionable Recommendations
                  </MlSectionTitle>

                  {!recommendations && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No additional recommendation data for this client yet.
                    </Typography>
                  )}

                  <Grid container spacing={3}>
                    {/* Opportunities */}
                    {(recommendations?.opportunities?.length ?? 0) > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography
                              variant="subtitle1"
                              gutterBottom
                              color="success.main"
                            >
                              Opportunities (
                              {(recommendations?.opportunities ?? []).length})
                            </Typography>
                            <Stack spacing={2}>
                              {(recommendations?.opportunities ?? []).map((opp, idx) => (
                                <Box key={idx}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      mb: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                    >
                                      {opp.title}
                                    </Typography>
                                    <Chip
                                      label={opp.priority}
                                      color={
                                        opp.priority === 'high'
                                          ? 'error'
                                          : 'default'
                                      }
                                      size="small"
                                    />
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {opp.description}
                                  </Typography>
                                  {opp.action && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      sx={{ mt: 0.5, fontStyle: 'italic' }}
                                    >
                                      Action: {opp.action}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {/* Threats */}
                    {(recommendations?.threats?.length ?? 0) > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography
                              variant="subtitle1"
                              gutterBottom
                              color="error.main"
                            >
                              Threats ({(recommendations?.threats ?? []).length})
                            </Typography>
                            <Stack spacing={2}>
                              {(recommendations?.threats ?? []).map((threat, idx) => (
                                <Box key={idx}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      mb: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                    >
                                      {threat.title}
                                    </Typography>
                                    <Chip
                                      label={threat.priority}
                                      color={
                                        threat.priority === 'high'
                                          ? 'error'
                                          : 'default'
                                      }
                                      size="small"
                                    />
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {threat.description}
                                  </Typography>
                                  {threat.action && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      sx={{ mt: 0.5, fontStyle: 'italic' }}
                                    >
                                      Action: {threat.action}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {/* Best Practices */}
                    {(recommendations?.best_practices?.length ?? 0) > 0 && (
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Best Practices from Top Performers
                            </Typography>
                            <Stack spacing={2}>
                              {(recommendations?.best_practices ?? []).map(
                                (practice, idx) => (
                                  <Box key={idx}>
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                    >
                                      {practice.practice} - {practice.source}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {practice.description}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      sx={{ mt: 0.5, fontStyle: 'italic' }}
                                    >
                                      Action: {practice.action}
                                    </Typography>
                                  </Box>
                                )
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {/* Action Items */}
                    {recommendations && (recommendations?.action_items?.length ?? 0) > 0 && (
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Action Items
                            </Typography>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Priority</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Timeline</TableCell>
                                    <TableCell>Description</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(recommendations?.action_items ?? []).map(
                                    (item, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>
                                          <Chip
                                            label={item.priority}
                                            color={
                                              item.priority === 'high'
                                                ? 'error'
                                                : 'default'
                                            }
                                            size="small"
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Typography
                                            variant="body2"
                                            fontWeight="medium"
                                          >
                                            {item.action}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2">
                                            {item.timeline}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            {item.description}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    )
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Task 4.2: Model Monitoring (A/B Evaluation) Tab */}
              {activeTab === 10 && (
                <MlModelMonitoringTab
                  driftAlerts={driftAlerts}
                  abEvaluation={abEvaluation}
                  abEvaluationLoading={abEvaluationLoading}
                  weeklyLearnings={weeklyLearnings}
                  weeklyLearningsLoading={weeklyLearningsLoading}
                  weeklyLearningsDays={weeklyLearningsDays}
                  onWeeklyLearningsPeriodChange={handleWeeklyLearningsPeriodChange}
                  captionEditLearning={captionEditLearning}
                  captionEditLearningLoading={captionEditLearningLoading}
                  onRefresh={() => {
                    loadABEvaluation();
                    loadDriftAlerts();
                    loadWeeklyLearnings();
                    loadCaptionEditLearning();
                  }}
                  onTriggerBackfill={handleTriggerBackfill}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}
      </ClientScopedGate>

      {/* Gap Analysis Issues Modal */}
      <Dialog
        open={gapModalOpen}
        onClose={() => setGapModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {gapModalData?.title || 'SEO Issues'}
          {gapModalData?.clientScore !== undefined && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Your Score: {(gapModalData.clientScore * 100).toFixed(0)}% |
                Competitor Avg:{' '}
                {(gapModalData.competitorScore * 100).toFixed(0)}%
              </Typography>
            </Box>
          )}
          {gapModalData?.wordCountGap !== undefined && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Your Words:{' '}
                {gapModalData.clientWordCount?.toLocaleString() || 0} |
                Competitor Avg:{' '}
                {gapModalData.competitorWordCount?.toLocaleString() || 0}
              </Typography>
            </Box>
          )}
          {gapModalData?.clientLinks !== undefined && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Your Links: {gapModalData.clientLinks || 0} | Competitor Avg:{' '}
                {Math.round(gapModalData.competitorLinks || 0)}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {gapModalData?.gaps && gapModalData.gaps.length > 0 ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {gapModalData.gaps.map((gap, idx) => (
                <Card key={idx} variant="outlined" sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontSize: '1rem', fontWeight: 'bold' }}
                    >
                      {gap.issue}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {gap.impact && (
                        <Chip
                          label={gap.impact}
                          size="small"
                          color={
                            gap.impact === 'critical'
                              ? 'error'
                              : gap.impact === 'high'
                              ? 'warning'
                              : gap.impact === 'medium'
                              ? 'info'
                              : 'default'
                          }
                        />
                      )}
                      {gap.effort && (
                        <Chip
                          label={`Effort: ${gap.effort}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>

                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {gap.client_value !== undefined && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Current:</strong> {gap.client_value}
                        </Typography>
                      </Box>
                    )}
                    {gap.competitor_avg && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Competitor Average:</strong>{' '}
                          {gap.competitor_avg}
                        </Typography>
                      </Box>
                    )}
                    {gap.recommended && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Recommended:</strong> {gap.recommended}
                        </Typography>
                      </Box>
                    )}
                    {gap.gap !== undefined && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Gap:</strong> {gap.gap}
                        </Typography>
                      </Box>
                    )}
                    {gap.action && (
                      <Box
                        sx={{
                          mt: 1,
                          p: 1.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color="primary.main"
                        >
                          <strong>Action Required:</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {gap.action}
                        </Typography>
                      </Box>
                    )}
                    {gap.expected_improvement && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="body2"
                          color="success.main"
                          fontWeight="medium"
                        >
                          <strong>Expected Improvement:</strong>{' '}
                          {gap.expected_improvement}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : gapModalData?.wordCountGap ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="info">
                <Typography variant="body1" gutterBottom>
                  <strong>Content Gap Identified</strong>
                </Typography>
                <Typography variant="body2">
                  Your website has{' '}
                  {gapModalData.clientWordCount?.toLocaleString() || 0} words on
                  average, while competitors average{' '}
                  {gapModalData.competitorWordCount?.toLocaleString() || 0}{' '}
                  words per page.
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, fontWeight: 'medium' }}
                >
                  You need to add approximately{' '}
                  {gapModalData.wordCountGap.toLocaleString()} more words to
                  match competitor content depth.
                </Typography>
              </Alert>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No specific issues found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGapModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Keyword Dialog */}
      <Dialog
        open={keywordDialogOpen}
        onClose={() => setKeywordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingKeyword ? 'Edit Keyword' : 'Add New Keyword'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Keyword"
              value={newKeyword.keyword}
              onChange={(e) =>
                setNewKeyword({ ...newKeyword, keyword: e.target.value })
              }
              placeholder="e.g., tattoo artist tampa"
              required
              fullWidth
            />
            <TextField
              label="Location (Optional)"
              value={newKeyword.location}
              onChange={(e) =>
                setNewKeyword({ ...newKeyword, location: e.target.value })
              }
              placeholder="e.g., Tampa, FL"
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={newKeyword.priority}
                label="Priority"
                onChange={(e) =>
                  setNewKeyword({ ...newKeyword, priority: e.target.value })
                }
              >
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Target Position (Optional)"
              type="number"
              value={newKeyword.target_position || ''}
              onChange={(e) =>
                setNewKeyword({
                  ...newKeyword,
                  target_position: e.target.value
                    ? parseInt(e.target.value)
                    : null,
                })
              }
              placeholder="e.g., 10 (for top 10)"
              fullWidth
              inputProps={{ min: 1, max: 100 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setKeywordDialogOpen(false);
              setEditingKeyword(null);
              setNewKeyword({
                keyword: '',
                location: '',
                priority: 'medium',
                target_position: null,
              });
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleAddKeyword} variant="contained">
            {editingKeyword ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Keyword Confirmation Dialog */}
      <Dialog
        open={deleteKeywordModal.open}
        onClose={handleDeleteKeywordCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete keyword</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete
            {deleteKeywordModal.keywordText ? (
              <>
                {' '}
                <strong>&quot;{deleteKeywordModal.keywordText}&quot;</strong>?
              </>
            ) : (
              ' this keyword'
            )}{' '}
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteKeywordCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteKeywordConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suggested Keywords Dialog */}
      <Dialog
        open={suggestionsOpen}
        onClose={() => setSuggestionsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          Suggested Keywords
          <MlEstimateBadge
            basis="keyword_suggestion"
            tooltip="Based on industry heuristics, not live platform data"
          />
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Based on this client&apos;s industry and location — heuristic
            suggestions, not live SERP or platform Insights. Add any you want to
            track. Volume and competition from Keyword Planner when configured.
          </Typography>
          {suggestionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : suggestions.length === 0 ? (
            <Typography color="text.secondary">
              No suggestions. Set client industry and city/state for better
              suggestions.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(Array.isArray(suggestions) ? suggestions : []).map((s, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 1,
                    px: 1,
                    borderRadius: 1,
                    bgcolor: s.already_added ? 'action.hover' : 'transparent',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography variant="body2" fontWeight="medium">
                        {s.keyword}
                      </Typography>
                      <MlEstimateBadge
                        basis="keyword_suggestion"
                        tooltip="Based on industry heuristics, not live platform data"
                      />
                    </Box>
                    {(s.location ||
                      s.avg_monthly_searches != null ||
                      s.competition) && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="span"
                        sx={{ display: 'block' }}
                      >
                        {s.location && `Location: ${s.location}`}
                        {(s.avg_monthly_searches != null || s.competition) && (
                          <>
                            {s.location && ' · '}
                            {s.avg_monthly_searches != null && (
                              <>~{s.avg_monthly_searches.toLocaleString()}/mo</>
                            )}
                            {s.avg_monthly_searches != null &&
                              s.competition &&
                              ' · '}
                            {s.competition && (
                              <>
                                Competition:{' '}
                                {String(s.competition)
                                  .replace(/_/g, ' ')
                                  .toLowerCase()}
                              </>
                            )}
                          </>
                        )}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    size="small"
                    variant={s.already_added ? 'outlined' : 'contained'}
                    disabled={s.already_added}
                    onClick={() => !s.already_added && handleAddSuggestion(s)}
                  >
                    {s.already_added ? 'Already added' : 'Add'}
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuggestionsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MLInsights;
